<template>
    <main class="w-full xl:w-5/6 max-w-1440 mx-auto">
        <div v-if="can" id="analytics">

            <div class="sticky top-0 z-50 flex flex-col items-center">
                <div class="absolute w-screen h-1/2 bg-primary-5 hide-from-print"></div>
                <div class="flex items-center rounded-6 bg-white p-24 shadow mt-32 space-x-24 w-full z-top">
                    <h1 class="text-primary font-semibold text-heading-2 first-letter:uppercase mr-auto h-fit my-auto">
                        {{ $t('analytics.title') }}
                    </h1>

                    <simple-button
                        icon="keyboard-arrow-left"
                        icon-position="left"
                        v-if="$route.params.id"
                        @click="goToJob()"
                    >
                        {{ $t('jobs.back_to.job') }}
                    </simple-button>

                    <AnalyticsPresets ref="presets" :filters="filterFields" @set-filters="setPresetFilters"/>

                    <simple-dropdown
                        :container="false"
                        class="pointer-events-auto"
                        trigger-class="h-full"
                        data-html2canvas-ignore position="right"
                    >
                        <template #trigger>
                            <round-button icon="more-vert" light />
                        </template>
                        <template #content="{ close }">
                            <nav
                                aria-orientation="vertical"
                                class="bg-white relative rounded shadow"
                                role="menu"
                            >
                                <dropdown-item
                                    :input-label="$t('analytics.export.export_to_csv')"
                                    @click="close(); $refs.export.startExporting()"
                                />
                                <dropdown-item @click="downloadPDF(close)">
                                    <span class="text-nowrap">{{ $t('analytics.export.export_to_pdf') }}</span>
                                    <loading v-if="fetchCount" type="progress" :progress="progress" size="small" />
                                </dropdown-item>
                            </nav>
                        </template>
                    </simple-dropdown>
                    <analytics-export
                        ref="export"
                        v-if="hasAccessToAllData"
                        :filters="filterFields"
                    />
                    <!-- Print button -->
                    <print-content
                        ref="print"
                        hide-button
                        :title="$t('analytics.title')" class="hidden"
                        content="analytics"
                    />
                </div>
            </div>

            <filters
                :default-filters="defaultFilters"
                :filterFields="filterFields"
                :filterTabs="filterTabs"
                @applyFilters="filterFields = $event; $refs.presets.clear();"
            ></filters>

            <overview
                :class="defaultCardClasses"
                :default-card-classes="defaultCardClasses.replace('min-h-full', 'h-full')"
                :filterData="filterData"
                :filterFields="filterFields"
                @data-loaded="fetchingDefaultData = false"
            ></overview>

            <div v-if="!fetchingDefaultData" :class="defaultCardClasses">
                <div>
                    <tabs :active-key="activeTab"
                          :items="tabs"
                          class="w-full mb-16"
                          @set-active="activeTab = $event"
                    ></tabs>
                </div>

                <div>
                    <template>
                        <tab-vacancies
                            v-if="activeTab === 'tabVacancies'"
                            ref="tabVacancies"
                            :default-card-classes="defaultCardClasses"
                            :filter-fields="filterFields"
                        ></tab-vacancies>
                        <tab-vacancy-trends
                            v-if="activeTab === 'tabVacancyTrends'"
                            ref="tabVacancyTrends"
                            :default-card-classes="defaultCardClasses"
                            :filter-fields="filterFields"
                        ></tab-vacancy-trends>
                        <tab-recruitment-process
                            v-if="activeTab === 'tabRecruitmentProcess'"
                            ref="tabRecruitmentProcess"
                            :default-card-classes="defaultCardClasses"
                            :filter-fields="filterFields"
                        ></tab-recruitment-process>
                        <tab-sources
                            v-if="activeTab === 'tabSources'"
                            ref="tabSources"
                            :default-card-classes="defaultCardClasses"
                            :filter-fields="filterFields"
                        ></tab-sources>
                    </template>
                </div>
            </div>
        </div>

        <error-403 v-else></error-403>
    </main>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import Overview from '../components/analytics/Overview.vue';
import Error403 from '../components/ui/Error403.vue';
import PrintContent from '../components/jobs/jobstats/PrintContent.vue';
import AnalyticsExport from "../components/AnalyticsExport.vue";
import SelectDropdown from "../components/forms/SelectDropdown";
import SvgIcon from '../components/ui/SvgIcon'
import Filters from "../components/Filters";
import Tabs from "../components/navigation/Tabs";
import SimpleDropdown from "../components/ui/SimpleDropdown.vue";

// tabs
import TabVacancies from "../components/analytics/TabVacancies";
import TabVacancyTrends from "../components/analytics/TabVacancyTrends.vue";
import TabRecruitmentProcess from "../components/analytics/TabRecruitmentProcess";
import TabSources from "../components/analytics/TabSources";

// apex charts
import Applications from '../components/analytics/Applications.vue';
import * as tags from "../services/TagService";
import Tag from "../models/Tag";
import {normalizeJson} from "../../shared/helpers";
import RoundButton from "../components/ui/RoundButton.vue";
import AnalyticsPresets from "../components/AnalyticsPresets.vue";
import {extendMoment} from "moment-range";
import Moment from "moment/moment";
import DropdownItem from "../components/ui/DropdownItem.vue";
import SimpleButton from "../components/ui/SimpleButton.vue";
import Loading from "../components/ui/Loading.vue";

import {mapStores} from "pinia";
import {useRepo} from "pinia-orm";
import {useAnalyticsStore} from "../state/modules/analytics";
import {useCompaniesStore} from "../state/modules/companies";
import {useMainStore} from "../state/store";

const defaultFilters = {
    withArchived: true,
    // withAnonymised: true,
    jobIds: [],
    locationIds: [],
    regions: [],
    responsibleUserIds: [],
    dateFromApplications: null,
    dateToApplications: null,
    dateFromJobs: null,
    dateToJobs: null,
    jobTagIds: [],
    applicationTagIds: [],
    requestReasonIds: [],
    rejectReasonIds: [],
    sourceIds: [],
    officeIds: [],
    filterGroups: [],
    filterGroupValues: [],
};
const moment = extendMoment(Moment)


export default {
    components: {
        Loading,
        SimpleButton,
        DropdownItem,
        SimpleDropdown,
        AnalyticsPresets,
        RoundButton,
        Applications,
        AnalyticsExport,
        'apexchart': VueApexCharts,
        'overview': Overview,
        'error-403': Error403,
        'print-content': PrintContent,
        SelectDropdown,
        Filters,
        Tabs,
        'svg-icon': SvgIcon,

        // tabs
        TabVacancies,
        TabVacancyTrends,
        TabRecruitmentProcess,
        TabSources,
    },
    data: function () {
        return {
            showFilter: false,
            refetching: false,
            fetchingDefaultData: true,
            dateLimitation: 3,
            filterFields: defaultFilters,
            fetchCount: 0,
            fetchedCount: 0,
            data: {
                jobs: null,
                applications: null,
                emails: null
            },
            filterData: {
                jobs: null,
                applications: null,
                emails: null
            },
            activeTab: 'tabVacancies',
            tabs: {
                'tabVacancies': {
                    'title': this.$t('analytics.tabs.vacancies'),
                    'icon': '',
                    'count': this.staleApplicationCount,
                    'applications': this.staleApplications,
                },
                'tabVacancyTrends': {
                    'title': this.$t('analytics.tabs.vacancy_trends'),
                    'icon': '',
                    'applications': this.newApplications,
                },
                'tabRecruitmentProcess': {
                    'title': this.$t('analytics.tabs.recruitment_process'),
                    'icon': '',
                    'count': this.newApplicationCount,
                    'applications': this.newApplications,
                },
                'tabSources': {
                    'title': this.$t('analytics.tabs.sources'),
                    'icon': '',
                    'count': this.interviewApplicationCount,
                    'applications': this.interviewApplications,
                },
            },
            defaultCardClasses: "w-full min-h-full rounded-6 bg-white border border-primary-20 p-24",
        };
    },
    computed: {
        ...mapStores(useMainStore, useAnalyticsStore, useCompaniesStore),
        progress() {
            return this.fetchedCount / this.fetchCount * 100;
        },
        getFrom() {
            return this.analyticsStore.getFrom;
        },
        getTo() {
            return this.analyticsStore.getTo;
        },
        getCompanyCreatedAt() {
            return this.analyticsStore.getCompanyCreatedAt;
        },
        filterTabs() {
            return {
                'filter-date': {title: 'general.date.title', icon: 'calendar-today'}, // limit is the amount of years a user can select between from and to date
                'filter-job-locations': {title: 'general.locations.title', icon: 'location-on'},
                'filter-job-responsible-user': {title: 'general.responsible-user.title', icon: 'person'},
                'filter-jobs': {title: 'general.jobs.title', icon: 'format-list-bulleted'},
                'filter-job-reasons': {title: 'settings.vacancy_reasons.title', icon: 'title'},
                'filter-application-source': {title: 'applications.source', icon: 'link'},
                'filter-offices': {title: 'general.offices.title', icon: 'location-city'},
                'filter-tags': {title: 'general.tags.title', icon: 'sell'},
                'filter-filter-groups': {title: 'general.filter_groups.title', icon: 'filter-alt'},
            }
        },
        from: {
            get() {
                return this.getFrom;
            },
            set(value) {
                this.analyticsStore.setFrom(value);
            }
        },
        to: {
            get() {
                return this.getTo;
            },
            set(value) {
                this.analyticsStore.setTo(value);
            }
        },
        companyCreatedAt: {
            get() {
                return this.getCompanyCreatedAt;
            },
            set(value) {
                this.analyticsStore.setCompanyCreatedAt(value);
            }
        },
        defaultFilters() {
            return defaultFilters;
        },
        can() {
            return this.$can('analytics.view');
        },
        hasAccessToAllData() {
            return this.can && this.$can('applications.manage') && this.$can('jobs.manage')
        },
    },
    methods: {
        goToJob() {
            this.$router.push({name: 'job', params: {id: this.$route.params.id}});
        },
        setPresetFilters(filters) {
            for (let [key, value] of Object.entries(filters)) {
                if (value && key.includes('date')) {
                    filters[key] = moment(value);
                }
            }
            this.filterFields = {
                ...defaultFilters,
                ...filters
            };
        },
        downloadPDF(callback) {
            const ref = this.activeTab,
                tab = this.$refs[ref];

            let promises = [];
            this.fetchCount = Object.keys(tab.$refs).length;

            if (ref) {
                Object.entries(tab.$refs).forEach(([key, value]) => {
                    if (!value.graphData) {
                        promises.push(value.fetchData().then(() => this.fetchedCount++));
                    } else {
                        this.fetchedCount++
                    }
                });
            } else {
                throw new Error('Something went wrong.');
            }

            setTimeout(() => {
                Promise.all(promises).then(() => {
                    this.$refs.print.printStats()
                        .finally(() => {
                            this.fetchCount = 0;
                            this.fetchedCount = 0;
                            callback();
                        });
                })
            }, 200)
        }
    },
    watch: {
        'filterFields.dateFromJobs'() {
            this.from = this.filterFields.dateFromJobs;
        },
        'filterFields.dateToJobs'() {
            this.to = this.filterFields.dateToJobs;
        },
        'filterFields.dateFromApplications'() {
            this.from = this.filterFields.dateFromApplications;
        },
        'filterFields.dateToApplications'() {
            this.to = this.filterFields.dateToApplications;
        }
    },
    created() {
        this.companyCreatedAt = moment(this.companiesModuleStore.creationDate()).startOf('day');
        this.from = this.companyCreatedAt;
        this.to = moment();
    },
    mounted() {
        if (this.$route.params.id) {
            this.filterFields.jobIds = [this.$route.params.id];
        }
        this.mainStore.setDocumentTitle(this.$t('analytics.title'));
        tags.index(true).then(result => useRepo(Tag).save(normalizeJson(result.data)));
    }
};
</script>
